import type { PhotoElement } from '../../shared/document';

export type PhotoDrawGeometry = {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  scaleX: number;
  scaleY: number;
  maxOffsetX: number;
  maxOffsetY: number;
};

export function getPhotoDrawGeometry(
  imageWidth: number,
  imageHeight: number,
  frameWidth: number,
  frameHeight: number,
  photo: Pick<PhotoElement, 'fit' | 'crop'>,
): PhotoDrawGeometry {
  const turns = photo.crop.quarterTurns;
  const orientedWidth = turns % 2 === 0 ? imageWidth : imageHeight;
  const orientedHeight = turns % 2 === 0 ? imageHeight : imageWidth;
  const baseScale = photo.fit === 'cover'
    ? Math.max(frameWidth / orientedWidth, frameHeight / orientedHeight)
    : Math.min(frameWidth / orientedWidth, frameHeight / orientedHeight);
  const scale = baseScale * (photo.fit === 'cover' ? photo.crop.zoom : 1);
  const width = imageWidth * scale;
  const height = imageHeight * scale;
  const rotatedWidth = turns % 2 === 0 ? width : height;
  const rotatedHeight = turns % 2 === 0 ? height : width;
  const maxOffsetX = photo.fit === 'cover' ? Math.max(0, (rotatedWidth - frameWidth) / 2) : 0;
  const maxOffsetY = photo.fit === 'cover' ? Math.max(0, (rotatedHeight - frameHeight) / 2) : 0;
  const offsetX = Math.max(-maxOffsetX, Math.min(maxOffsetX, photo.crop.offsetX));
  const offsetY = Math.max(-maxOffsetY, Math.min(maxOffsetY, photo.crop.offsetY));

  return {
    x: frameWidth / 2 + offsetX,
    y: frameHeight / 2 + offsetY,
    width,
    height,
    rotation: turns * 90,
    scaleX: photo.crop.flipX ? -1 : 1,
    scaleY: photo.crop.flipY ? -1 : 1,
    maxOffsetX,
    maxOffsetY,
  };
}

export function clampCropOffset(
  offsetX: number,
  offsetY: number,
  imageWidth: number,
  imageHeight: number,
  frameWidth: number,
  frameHeight: number,
  photo: Pick<PhotoElement, 'fit' | 'crop'>,
): { offsetX: number; offsetY: number } {
  const geometry = getPhotoDrawGeometry(imageWidth, imageHeight, frameWidth, frameHeight, photo);
  return {
    offsetX: geometry.maxOffsetX === 0 ? 0 : Math.max(-geometry.maxOffsetX, Math.min(geometry.maxOffsetX, offsetX)),
    offsetY: geometry.maxOffsetY === 0 ? 0 : Math.max(-geometry.maxOffsetY, Math.min(geometry.maxOffsetY, offsetY)),
  };
}
